import React from 'react'
import { makeAutoObservable } from 'mobx'
import * as R from 'ramda'
import { v4 as uuidv4 } from 'uuid'
import { capitalize, cloneDeep, startCase } from 'lodash'
import {
    Bar, Line, QuadrantAnalysis, LMX as MatrixChart,
} from 'frontend-charts'

import DataFormatter from './dataFormatter'
import { getQuestionTitle } from '../../utils/QuestionHelper'
import PPPresentation from './classes/Presentation'

import ENUM from '../PresentationV2/ENUM'
import { isEmpty, stripOutHtml } from '../helpers'
import chartRenderSettings from './chartRenderSettings'
import { sortBy, sortedIndexArray } from '../../utils/helpers'
import MergeFields from '../../utils/MergeFields'

const settingsDefaults = {
    sorting: 'noRanking',
    removeZero: false,
    removeBaseLimit: 0,
    excludeNonNumeric: true,
    showAverage: false,
}

export default class PowerpointBuilderStore {
    slideSelectModalOpen = false

    filterModalOpen = false

    verbatimSettingsModalVisible = false

    globalSettingsModalOpen = false

    slideSettingsModalOpen = false

    chartSettingsModalOpen = false

    derivedChartModalOpen = false

    inheritedSettingsModalVisible = false

    crossbreakSettingsModalVisible = false

    surveys = []

    survey = null

    search = ''

    questions = []

    filteredQuestions = []

    benchmarks = new Map()

    showAverageByQuestionId = new Map()

    sideNavigationOpen = ''

    currentSection = 0

    currentSlide = 0

    currentChart = null

    verbatimPosition = 0

    verbatimSlots = 0

    addPosition = null

    globalYears = []

    globalBenchmarks = []

    questionData = new Map()

    chartData = new Map()

    benchmarkPeriod = 'ROL-36'

    editing = false

    defaultLabelPositions = {
        bar: 'right',
        default: 'top',
    }

    derivedChartModalSelections = {
        question: '',
        kpi: '',
        base: 0,
    }

    inheritedChartModalSelections = {
        parent: '',
        child: '',
        ranking: 'noRanking',
        base: 0,
        average: 'average',
        removeZero: false,
    }

    crossbreakChartModalSelections = {
        questions: [
            {
                question: '',
                ranking: 'noRanking',
                base: 0,
            },
        ],
        kpi: '',
        benchmarks: [],
    }

    product = {
        name: '',
        edition: '',
    }

    kpis = {
        OS: false,
        FA: false,
        NPS: false,
        IOV: false,
    }

    oidMap = {
        industry: 1,
        company: 3,
        division: 4,
        portfolio: 5,
        sector: 1,
    }

    possibleBenchmarks = {
        3: ['industry', 'client'],
        4: ['industry', 'client', 'division'],
        5: ['industry', 'client', 'division', 'portfolio'],
        6: ['industry', 'client', 'division', 'portfolio', 'sector'],
    }

    chartFilterOptions = [
        { label: 'No ranking', value: 'noRanking' },
        { label: 'Low to high', value: 'lowToHigh' },
        { label: 'High to low', value: 'highToLow' },
        { label: 'A to Z', value: 'aToZ' },
        { label: 'Z to A', value: 'zToA' },
    ]

    chartTypeOptions = [
        {
            label: 'Verbatim',
            value: 'verbatim',
            filter: (questionType, kpiType) => (
                ['TA', 'TX'].includes(questionType)
            ),
        },
        {
            label: 'KPI chart',
            value: 'kpiChart',
            filter: (questionType, kpiType, hasNumericCodes) => (
                (questionType === 'RD' && hasNumericCodes)
                || (kpiType && kpiType !== 'NPS')
            ),
        },
        {
            label: 'NPS chart',
            value: 'npsChart',
            filter: (questionType, kpiType) => (
                kpiType === 'NPS'
            ),
        },
        {
            label: 'Column chart',
            value: 'standardColumn',
            filter: (questionType, kpiType) => (
                ['OD', 'RD', 'CH', 'SB', 'PC'].includes(questionType)
            ),
        },
        {
            label: 'Bar chart',
            value: 'standardBar',
            filter: (questionType, kpiType) => (
                ['OD', 'RD', 'CH', 'SB', 'PC'].includes(questionType)
            ),
        },
        {
            label: 'Stacked bar',
            value: 'stackedBar',
            filter: (questionType, kpiType) => (
                ['TB', 'SG'].includes(questionType)
            ),
        },
        {
            label: 'Stacked column',
            value: 'stackedColumn',
            filter: (questionType, kpiType) => (
                ['TB', 'SG'].includes(questionType)
            ),
        },
    ]

    benchmarkPeriods = [
        {
            value: 'ROL-36',
            label: '36 Months',
            benchmarkType: 'ROL',
            period: { periodType: 'ROL', duration: 36 },
            ossPeriod: { periodType: 'last', interval: 'MONTH', duration: 36 },
        },
        {
            value: 'ROL-84',
            label: 'Long-term',
            benchmarkType: 'ROL',
            period: { periodType: 'ROL', duration: 84 },
            ossPeriod: { periodType: 'last', interval: 'MONTH', duration: 84 },
        },
    ]

    benchmarkOptions = {
        company: false,
        division: false,
        industry: false,
        portfolio: false,
        sector: false,
    }

    presentation = null

    constructor(store) {
        this.appStore = store
        this.surveyYear = this.getSurveyYear()
        this.init()
        const pres = new PPPresentation({
            id: uuidv4(),
            clientName: store.userData.clientName,
            locale: store.survey.metadata.defaultLocale,
            benchmarkPeriod: this.benchmarkPeriods.find(({ value }) => value === this.benchmarkPeriod)?.period,
            section: [
                {
                    slide: [
                        {
                            title: '',
                            template: 'title',
                        },
                    ],
                },
            ],
        }, this.appStore)

        this.presentation = pres
        makeAutoObservable(this)
    }

    save = async () => {
        const url = await this.appStore.http.post('/export/ppt/v2', JSON.stringify(this.presentation))
        const a = document.createElement('a')
        a.href = url
        a.download = 'ppt.pptx'
        document.body.appendChild(a)
        a.click()
        a.remove()
    }

    init = async () => {
        const [
            products,
            portfolios,
            divisions,
            globalYears,
            benchmarkSet,
        ] = await Promise.all([
            this.getProducts(),
            this.getPortfolios(),
            this.getDivisions(),
            this.getGlobalYears(),
            this.getBenchmarks(),
            this.getProduct(),
            this.getSurveys(),
        ])

        this.globalYears = globalYears
        const questionYears = await this.getQuestionYears()
        const latestSurveys = Object.values(this.surveys
            .filter((survey) => (
                survey.status !== 4
                    && [1, 2, 3, 6].includes(survey.productStatus)
                    && survey.isPrimary
                    && survey.surveyType !== 'GE'
            ))
            .reduce((map, survey) => {
                const { respondentType, surveyId, year } = survey
                map[respondentType] ||= {
                    year: 0,
                }

                if (year > map[respondentType].year) {
                    map[respondentType] = {
                        surveyId,
                        year,
                    }
                }

                return map
            }, {})).map(({ surveyId }) => surveyId)
        const benchmarkTags = await this.getBenchmarkTags(latestSurveys)
        this.questionYears = questionYears
        this.questionBenchmarks = benchmarkSet
        await this.handleSelectSurvey({ value: this.appStore.survey.id.toUpperCase() })
        this.globalBenchmarks = this.getGlobalBenchmarks(this.questions, benchmarkSet)
        this.productNames = products
        this.portfolioNames = portfolios
        this.divisionNames = divisions
        this.benchmarkTags = this.formatBenchmarkTags(benchmarkTags)
        this.tagBenchmarks = this.getTagBenchmarks(benchmarkTags)

        this.tagBenchmarks.forEach(({ value, label, shortLabel }) => {
            // this.benchmarkLabels[value] = label
            this.benchmarks.set(value, false)
            // this.tagBenchmarkLabels[value] = shortLabel
        })

        const tagNames = benchmarkTags.reduce((tn, bt) => {
            bt.tagOptions.forEach((top) => {
                const config = JSON.parse(top.tagOptionConfig)
                tn = {
                    ...tn,
                    [top.tagOptionId]: R.pathOr(
                        R.pathOr('', ['langs', 'en_GB'], config), ['name'], config,
                    ),
                }
            })
            return tn
        }, {})
        this.tagNames = tagNames

        this.dataFormatter = new DataFormatter(
            this.appStore,
            this.productNames,
            this.portfolioNames,
            this.divisionNames,
            this,
            tagNames,
        )
    }

    get activeSection() {
        return this.presentation.section[this.currentSection]
    }

    get activeSlide() {
        return this.activeSection?.slide[this.currentSlide]
    }

    get activeChart() {
        return this.activeSlide.question.get(this.currentChart)
    }

    get sections() {
        return this.presentation.section
    }

    get derivedKpiQuestions() {
        return this.questions
            .filter((q) => ['RD', 'SB']
                .includes(q.question.questionTypeCode) && !!(q.question.optionSet.hasNumericCodes))
    }

    get derivedQuestions() {
        return this.questions
            .filter((q) => q.question.questionTypeCode === 'TB' && !!(q.question.optionSetX.hasNumericCodes))
    }

    get activeSlideTitle() {
        const slideQuestionShorts = Array.from(this.activeSlide.question?.values()).map((q) => q.questionShortText)

        return slideQuestionShorts.join('/')
    }

    get activeSlideRespondentTypes() {
        const slideQuestionRespTypes = Array.from(new Set(Array.from(this.activeSlide.question?.values()).map((q) => q.respondentType)))

        return slideQuestionRespTypes.map((t) => (t === 'EX' ? 'Exhibitor' : 'Visitor')).join('/')
    }

    get surveyOptions() {
        return this.surveys.map((s) => ({ value: s.surveyId, label: s.surveyName }))
    }

    get activeChartData() {
        if (this.activeChart?.kpiType === 'LMX') {
            return this.chartData.get(`${this.activeChart.surveyId}-LMX`)
        }
        if (this.activeChart?.kpiType === 'OSS') {
            return this.chartData.get(`${this.activeChart.surveyId}-OSS`)
        }
        return this.chartData.get(this.activeChart?.questionId)
    }

    chartTypesForQuestion({ questionTypeCode, kpiType, hasNumericCodes }) {
        return this.chartTypeOptions.filter(({ filter }) => filter(questionTypeCode, kpiType, hasNumericCodes))
    }

    get questionYearsForChart() {
        if (this.activeChart?.kpiType === 'LMX') {
            return Object.keys(this.globalYears).map(Number).reverse()
        }
        if (this.activeChart?.kpiType === 'OSS') {
            return Object.keys(this.globalYears).map(Number).reverse()
        }
        return [...new Set(this.questionYears[this.activeChart?.questionId]?.map((qy) => qy.label))]
            .filter((y) => y !== this.activeChart?.year)
    }

    get presentationComparisonYears() {
        const comps = {}
        Object.keys(this.globalYears).forEach((gy) => comps[gy] =this.globalYears[gy].map((y) => y.checked).every((v) => !!v))
        return comps
    }

    get inheritedChildQuestions() {
        return this.questions.filter((q) => !!q.inheritedPageQuestionId)
    }

    get inheritedParentQuestion() {
        return this.questions.find((q) => q.question.id === this.inheritedChartModalSelections.parent)
    }

    get crossbreakCapableQuestions() {
        return this.questions.filter((q) => ['RD', 'TB', 'CH', 'SB'].includes(q.question.questionTypeCode))
    }

    getChartDataForQuestion(qid, pos) {
        const chartData = this.chartData.get(qid)
        if (!chartData) return []

        const ppQuestion = this.activeSlide?.question.get(pos)
        let baseData = chartData?.data.filter((d) => !d.benchmarkName)

        baseData = baseData.map((d) => ({
            ...d,
            show: ppQuestion.comparisonYears.includes(d.year) || ppQuestion.year === d.year,
        }))
        const qbench = this.questionBenchmarks?.benchmarks[qid]

        if (!qbench || !ppQuestion?.benchmark?.length) {
            return {
                ...chartData,
                data: baseData,
            }
        }

        const chartbench = this.globalBenchmarks.filter((gb) => ppQuestion?.benchmark.includes(gb.value))
        chartbench.forEach((gb) => {
            if (['NPS', 'pct'].includes(chartData.kpiType) && qbench[gb.value]?.pct) {
                baseData.push({
                    data: qbench[gb.value].pct,
                    benchmarkName: capitalize(gb.value),
                    benchmarkPeriod: this.benchmarkPeriod,
                })
            }
        })

        return {
            ...chartData,
            data: baseData,
        }
    }

    getSurveys = async () => {
        const { appStore } = this
        const { survey } = appStore
        const res = await appStore.http.get('/survey')
        const matchingSurv = res.filter((s) => s.year <= survey.getYear() && s.surveyType === R.pathOr('', ['metadata', 'taxonomy', 'surveyType'], survey) && s.productId === R.pathOr(0, ['metadata', 'productInfo', 'productId'], survey))
        this.surveys = matchingSurv
    }

    getGlobalBenchmarks = (slideQuestions, benchmarkSet) => {
        const { appStore, oidMap } = this
        const {
            availableBenchmarks,
        } = benchmarkSet

        const questionOids = Array.from(this.questions.reduce((set, { question }) => (
            set.add(question.metadata.oid)
        ), new Set()))

        const benchmarks = Object.entries(availableBenchmarks)
            .filter(([benchmark, isAvailable]) => (
                isAvailable
                    && R.pathOr(false, ['userData', 'benchmarks', benchmark], appStore)
                    && (
                        (['industry', 'sector'].includes(benchmark) && questionOids.some((oid) => oid === 1))
                        || (benchmark !== 'industry' && questionOids.some((oid) => oid <= oidMap[benchmark]))
                    )
            ))
            .map(([benchmark]) => ({
                name: ENUM().benchmarks,
                value: benchmark,
                checked: ['industry', 'company'].includes(benchmark),
                oid: benchmark === 'sector' ? 6 : oidMap[benchmark],
                label: capitalize(benchmark),
            }))

        return R.sortWith(
            [R.ascend(R.prop('oid'))],
            benchmarks,
        )
    }

    getSurveyYear = () => {
        const { appStore } = this
        const { survey } = appStore

        const endDate = R.pathOr(null, ['metadata', 'productInfo', 'industrySpecificInfo', 'endDate'], survey)
        const closeDate = R.pathOr(null, ['metadata', 'closeDate'], survey)

        return new Date(R.or(endDate, closeDate)).getFullYear()
    }

    getDetailsType = (data, nonQuestionType) => {
        const responseType = R.pathOr('', ['responseType'], data)
        if (R.isNil(nonQuestionType) || nonQuestionType !== 'KPI') return responseType

        const dataType = R.pathOr(0, ['data', 0, 'data'], data)

        return typeof dataType === 'object' ? 'pct' : 'avg'
    }

    handleChangeChartType = (e) => {
        const qid = this.activeChart?.questionId
        const chartData = this.chartData.get(qid)
        this.chartData.set(qid, {
            ...chartData,
            widgetRenderSettings: {
                ...chartData.widgetRenderSettings,
                labelPosition: e.value === 'standardBar'
                    ? this.defaultLabelPositions.bar : this.defaultLabelPositions.default,
            },
        })
    }

    updateRemoveZeroNull = (e) => {
        const ac = this.activeChart
        const qid = ac?.questionId
        const chartData = this.chartData.get(qid)
        let newValue = ''

        if (e.name === 'removeZero') {
            if (e.value) {
                newValue = ac.settings.excludeNonNumeric ? 'both' : 'null'
            } else {
                newValue = ac.settings.excludeNonNumeric ? 'null' : 'dontRemove'
            }
        }

        if (e.name === 'excludeNonNumeric') {
            if (e.value) {
                newValue = ac.settings.removeZero ? 'both' : 'zero'
            } else {
                newValue = ac.settings.removeZero ? 'zero' : 'dontRemove'
            }
        }
        this.chartData.set(qid, {
            ...chartData,
            widgetRenderSettings: {
                ...chartData.widgetRenderSettings,
                removegetChartDataForQuestionZeroNull: newValue,
            },
        })
    }

    getCrossTabChartData = async (ppQuestion) => {
        const { appStore } = this

        const resp = await appStore.http.post('/export/ppt/crossbreak', JSON.stringify({
            locale: 'en_GB',
            benchmarkPeriod: { periodType: 'ROL', duration: 36 },
            question: ppQuestion,
        }))

        if (!resp) return

        const barData = []

        R.pathOr([], ['barData', 'labels'], resp).forEach((l, i) => {
            barData.push({
                name: l,
                value: R.pathOr(null, ['barData', 'values', i], resp),
            })
        })

        const lineData = []

        R.pathOr([], ['lineData'], resp).forEach((ld) => {
            const d = []
            R.pathOr([], ['labels'], ld).forEach((l, i) => (
                d.push({
                    name: l,
                    value: R.pathOr(null, ['values', i], ld),
                })
            ))
            lineData.push({
                data: d,
                name: ld.name,
            })
        })

        this.chartData.set(`${ppQuestion.questionId}-crossbreak`, {
            data: [{
                data: barData,
                name: ppQuestion.questionShortText,
                tagOptionId: undefined,
            }],
            lineData,
            kpiType: 'crossbreak',
            nonQuestionType: 'crossbreak',
            details: { type: 'nps' },
            options: [],
            columns: [],
            isCurrency: false,
            benchmarkType: 'YOY',
            scope: 'product',
            widgetRenderSettings: {
                showLegend: true,
                showDataLabels: true,
                labelPosition: this.defaultLabelPositions.default,
                showOptionLabels: true,
                rotateXAxisLabels: false,
                orientation: 'horizontal',
                swapSeriesXAxis: false,
                format: '',
                sortBy: 'none',
                sortDirection: -1,
                removeZeroNull: 'dontRemove',
                dataDecimals: 2,
                showTitle: false,
                showYAxis: true,
                scrollable: false,
            },
        })
    }

    getInheritedChartData = async (parent, child) => {
        const { appStore } = this
        const reqData = [
            {
                benchmarkType: 'YOY',
                groupId: '',
                industry: this.survey.industry,
                productId: this.survey.productId,
                productType: this.survey.producttypecode,
                respondentType: this.survey.respondentType,
                responseType: 'pct',
                scope: 'survey',
                surveyType: this.survey.surveyType,
                questionId: parent.question.id,
                removeBaseLimit: 2,

            },
            {
                benchmarkType: 'YOY',
                groupId: '',
                industry: this.survey.industry,
                productId: this.survey.productId,
                productType: this.survey.producttypecode,
                respondentType: this.survey.respondentType,
                responseType: 'avg',
                scope: 'survey',
                surveyType: this.survey.surveyType,
                questionId: child.question.id,
                parentId: parent.question.id,
                removeBaseLimit: 2,
            },
        ]

        const resp = await appStore.http.post('/report/v1/dashboard', JSON.stringify({
            widget: reqData,
        }))

        if (!resp) return

        const barData = []
        const lineData = []

        R.pathOr([], ['widget', 0, 'options'], resp).forEach(([optId, label]) => {
            barData.push({
                name: label,
                value: R.pathOr(null, ['data', optId],
                    (R.pathOr([], ['widget', 0, 'data'], resp).find((d) => d.year === this.surveyYear))),
            })
            lineData.push({
                name: label,
                value: R.pathOr(null, ['data', optId],
                    (R.pathOr([], ['widget', 1, 'data'], resp).find((d) => d.year === this.surveyYear))),
            })
        })

        this.chartData.set(`${parent.question.id}-inherited`, {
            ...R.pathOr({}, ['widget', 0], resp),
            data: [{
                benchmark: undefined,
                benchmarkName: undefined,
                data: barData,
                name: this.getTitle(parent.question),
                tagOptionId: undefined,
                tagOptionName: undefined,
            }],
            lineData: [{
                data: lineData,
                name: this.getTitle(child.question),
            }],
            kpiType: 'inherited',
            nonQuestionType: 'inherited',
            details: { type: 'avg' },
            options: [],
            columns: [],
            isCurrency: false,
            benchmarkType: 'YOY',
            scope: 'product',
            widgetRenderSettings: {
                dataDecimals: 2,
            },
        })
    }

    getDerivedChartData = async (ppQuestion) => {
        const { appStore } = this

        const resp = await appStore.http.post('/export/ppt/derived', JSON.stringify({ ...ppQuestion, store: undefined, ppStore: undefined }))

        if (!resp) return

        const data = []

        R.pathOr([], ['xValues', 'labels'], resp).forEach((l, i) => {
            data.push({
                x: R.pathOr(null, ['xValues', 'values', i], resp),
                y: R.pathOr(null, ['yValues', 'values', i], resp),
                z: R.pathOr(null, ['yValues', 'sizes', i], resp),
                name: l,
                series: l,
            })
        })

        this.chartData.set(`${ppQuestion.questionId}-${ppQuestion.kpiQuestionId}`, {
            data,
            kpiType: 'quadrantAnalysis',
            nonQuestionType: 'quadrantAnalysis',
            dataDetails: { type: 'avg' },
            options: [],
            columns: [],
            isCurrency: false,
            quadrantCurrency: { yAxis: false, xAxis: false },
            benchmarkType: 'YOY',
            scope: 'product',
            widgetRenderSettings: {
                showLabels: false,
                showLegend: true,
                showTitle: false,
                yAxisTitle: `${ppQuestion.kpiQuestionShortText} Average score`,
            },
        })
    }

    getResponseTypeForQuestion = (question) => {
        if (question.question.metadata.kpiType === 'NPS') {
            return this.showAverageByQuestionId.get(question.question.id) ? 'nps' : 'pct'
        }
        if (['RD', 'CH', 'SB', 'TB'].includes(question.question.questionTypeCode)) {
            return this.showAverageByQuestionId.get(question.question.id) ? 'avg' : 'pct'
        }
        return 'avg'
    }

    getParentOptions = (parentId) => {
        if (!parentId) return []

        const parentQuestion = this.questions.find((qq) => qq.pageQuestionId === parentId.toUpperCase())

        if (!parentQuestion) return []

        return this.getParentOptions(parentQuestion.inheritedPageQuestionId).concat(parentQuestion.question.optionSet.options.filter(({ isNonReporting }) => !isNonReporting))
    }

    getQuestionData = async (questions) => {
        const { appStore } = this
        const reqData = []
        const groupIds = []

        questions.forEach((q) => {
            const groupId = uuidv4()
            const { questionTypeCode, optionSetX, optionSet } = q.question
            const options = (optionSetX ?? optionSet)?.options ?? []
            const parentQuestion = this.questions.find((qq) => qq.pageQuestionId === q.inheritedPageQuestionId?.toUpperCase())
            const parentId = parentQuestion?.question?.id
            groupIds.push(groupId)
            reqData.push({
                benchmarkType: 'YOY',
                groupId,
                industry: this.survey.industry,
                productId: this.survey.productId,
                productType: this.survey.producttypecode,
                respondentType: this.survey.respondentType,
                responseType: this.getResponseTypeForQuestion(q),
                scope: 'survey',
                surveyType: this.survey.surveyType,
                questionId: q.question.id,
                parentId,
            })
            if (options.some(({ reportingValue }) => !!reportingValue)) {
                reqData.push({
                    benchmarkType: 'YOY',
                    groupId,
                    industry: this.survey.industry,
                    productId: this.survey.productId,
                    productType: this.survey.producttypecode,
                    respondentType: this.survey.respondentType,
                    responseType: q.question.metadata.kpiType === 'NPS' ? 'nps' : 'avg',
                    scope: 'survey',
                    surveyType: this.survey.surveyType,
                    questionId: q.question.id,
                    parentId,
                })
            }
        })

        const resp = await appStore.http.post('/report/v1/dashboard', JSON.stringify({
            widget: reqData,
        }))

        if (!resp) return

        questions.forEach((q, i) => {
            const groupId = groupIds[i]
            const { question, inheritedPageQuestionId } = q
            const { questionTypeCode } = question
            const [qdata, averageData] = resp.widget.filter((w) => w.groupId === groupId)
            const parentOptions = this.getParentOptions(inheritedPageQuestionId)
            this.questionData.set(q, qdata)

            let averages

            const baseSizes = qdata.data.reduce((map, { year, baseSize }) => ({
                ...map,
                [year]: baseSize,
            }), {})

            if (questionTypeCode === 'TB') {
                // if no numerical values for the question use total pct per row as avg so removeZero logic doens't need to be updated
                const data = averageData
                    ? averageData.data
                    : qdata.data.map(({ year, data }) => ({
                        year,
                        data: Object.entries(data).reduce((map, [k, v]) => {
                            const [key] = k.split('_')
                            map[key] ??= 0
                            map[key] += v
                            return map
                        }, {}),
                    }))
                const rows = parentOptions.concat(question.optionSetY.options)
                averages = data.reduce((map, { year, data }) => ({
                    ...map,
                    [year]: rows.map(({ id }) => data[id]),
                }), {})
            } else if (['RD', 'SB'].includes(questionTypeCode) && averageData) {
                const fData = averageData.data.map(({ year, data }) => ({
                    name: year,
                    data: [{ value: data, name: year }],
                }))
                const xAxisLabels = ['Average']
                averages = {
                    fData,
                    xAxisLabels,
                }
            }

            const pctFormat = this.getDetailsType(qdata) === 'pct' ? '%' : 'numeric'
            const otherOrExclusiveOptionIds = new Set(
                (question.optionSet?.options ?? [])
                    .filter(({ hasOptionOther, isExclusive }) => hasOptionOther || isExclusive)
                    .map(({ id }) => id)
            )
            const otherOrExclusiveLabels = new Set(
                (qdata.options ?? [])
                    .filter(([id]) => otherOrExclusiveOptionIds.has(id))
                    .map(([, label]) => stripOutHtml(label))
            )

            const d = {
                ...this.questionData.get(q),
                dataDetails: {
                    type: this.getDetailsType(qdata),
                },
                options: qdata.options?.filter((o) => o !== null),
                otherOrExclusiveLabels,
                columns: qdata.columns?.filter((o) => o !== null),
                responses: qdata.responses ?? {},
                min: qdata.min || undefined,
                max: qdata.max || undefined,
                averages,
                percentiles: qdata.percentiles || undefined,
                isCurrency: qdata.isCurrency,
                quadrantCurrency: undefined,
                nonQuestionType: q.question.questionTypeCode === 'TB' ? '' : 'KPI',
                questionInitialLocale: q.question.metadata.initialLocale,
                questionTypeCode: q.question.questionTypeCode,
                questionOptions: parentOptions.concat(R.pathOr([], ['optionSetY', 'options'], q.question)),
                kpiType: this.getDetailsType(qdata),
                benchmarkType: '',
                baseSizes,
                widgetRenderSettings: {
                    showLegend: true,
                    showDataLabels: true,
                    labelPosition: this.defaultLabelPositions.default,
                    showOptionLabels: true,
                    rotateXAxisLabels: false,
                    orientation: 'horizontal',
                    swapSeriesXAxis: false,
                    format: qdata.isCurrency ? 'currency' : pctFormat,
                    sortBy: q.question.questionTypeCode === 'CH' ? 'value' : 'none',
                    sortDirection: -1,
                    removeZeroNull: 'dontRemove',
                    dataDecimals: this.getDetailsType(qdata) === 'pct' ? 0 : 2,
                    showTitle: false,
                    showYAxis: true,
                    scrollable: false,
                },
            }

            this.chartData.set(q.question.id, d)
        })
    }

    getGlobalYears = async () => {
        const { appStore, surveyYear } = this
        const { survey, client: { prodductMap } } = appStore
        const { productId, industrySpecificInfo: { productType, industry } } = survey.metadata.productInfo
        const { surveyType } = survey.metadata.taxonomy

        const allSurveyRespTypes = Object
            .keys(R.pathOr({}, [industry, 'children', productType, 'children', surveyType, 'children'], prodductMap))
        const arr = await Promise.all(allSurveyRespTypes
            .map(async (r) => appStore.http.get(`/product/${productId}/surveyyears/${surveyType}/${r}`)))

        const allSurveyYears = arr.flat()

        if (isEmpty(allSurveyYears)) {
            throw new Error('No results')
        }

        const mappedYears = allSurveyYears
            .filter(({ year }) => (
                Number(year) !== surveyYear
                && !Number.isNaN(Number(year))
            ))
            .reduce((map, { year, surveyId }) => (
                !Object.hasOwnProperty.call(map, year)
                    ? Object.assign(map, {
                        [year]: [
                            {
                                name: ENUM().comparisonSurveyIds,
                                value: surveyId.toUpperCase(),
                                label: String(year),
                                year: Number(year),
                                checked: (survey.getYear() - Number(year)) <= 2,
                            },
                        ],
                    })
                    : Object.assign(map, {
                        [year]: [
                            ...map[year],
                            {
                                name: ENUM().comparisonSurveyIds,
                                value: surveyId.toUpperCase(),
                                label: String(year),
                                year: Number(year),
                                checked: (survey.getYear() - Number(year)) <= 2,
                            },
                        ],
                    })
            ), {})

        return Object.fromEntries(
            Object.entries(mappedYears).sort(([a, _], [valB, __]) => Number(valB) - Number(a)),
        )
    }

    formatBenchmarkTags = (benchmarkTags) => {
        const { langcode } = this.appStore
        return benchmarkTags.map((tag) => {
            const newTagOptions = tag.tagOptions.map((top) => {
                const parsedTagConfig = JSON.parse(top.tagOptionConfig)
                const tagName = R.pathOr(
                    R.pathOr(
                        R.pathOr('', ['langs', 'en_GB'], parsedTagConfig),
                        ['langs', langcode],
                        parsedTagConfig,
                    ),
                    ['name'], parsedTagConfig,
                )
                return {
                    ...top,
                    tagOptionName: tagName,
                }
            })
            return { ...tag, tagOptions: newTagOptions }
        })
    }

    getBenchmarkTags = async () => {
        if (process.env.USE_CUSTOM_BENCHMARKS !== 'true') return []
        const response = await this.appStore.http.get('/benchmarkTags')
        return response.benchmarkTags
    }

    getProducts = async () => {
        const { appStore } = this
        try {
            const products = await appStore.http.get('/product')
            this.products = products
            return products.reduce((obj, p) => {
                if (this.oId === 6 && p.productId === this.recordId) {
                    this.productType = p.productType
                }
                return { ...obj, [p.productId]: `${p.name}${p.edition ? ` - ${p.edition}` : ''}` }
            }, {})
        } catch (e) {
            return {}
        }
    }

    getPortfolios = async () => {
        const { appStore } = this
        const { userData } = appStore
        const { role } = userData
        try {
            const portfolios = role > 130 && role !== 150 ? [] : await appStore.http.get('/portfolio')
            this.portfolios = portfolios
            return portfolios.reduce((obj, p) => ({ ...obj, [p.portfolioId]: p.name }), {})
        } catch (e) {
            this.portfolios = []
            return {}
        }
    }

    getDivisions = async () => {
        const { appStore } = this
        const { userData } = appStore
        const { role } = userData
        try {
            const divisions = role > 120 && role !== 150 ? [] : await appStore.http.get('/division')
            this.divisions = divisions
            return divisions.reduce((obj, p) => ({ ...obj, [p.divisionId]: p.name }), {})
        } catch (e) {
            this.divisions = []
            return {}
        }
    }

    getBenchmarks = async () => {
        const { appStore } = this
        const { survey } = appStore

        let benchmarks = {
            availableBenchmarks: {},
        }

        try {
            benchmarks = await appStore.http.get(`/v2/survey/${survey.id}/report/benchmark?period=ROL&duration=36`)
        } catch (e) {
            console.error(e)
            appStore.addNotification(null, 'Error loading benhcmarks', 'error')
        }

        return benchmarks
    }

    getProduct = async () => {
        const { appStore } = this

        try {
            const products = await appStore.http.get('/product')

            const presProd = products.find((product) => product.productId === appStore.survey.metadata.productInfo.productId)

            if (presProd) {
                this.product = {
                    name: presProd.name,
                    edition: presProd.edition,
                }
            }
        } catch (e) {
            console.error(e)
        }
    }

    getQuestionYears = async () => {
        const { appStore } = this
        const { survey } = appStore
        const questionYears = await appStore.http.get(`/survey/${survey.id}/yearsbenchmarks`)
        const globalYearStrings = Object.keys(this.globalYears).map((gy) => Number(gy))
        const globalChecked = {}
        Object.keys(this.globalYears).forEach((gy) => globalChecked[gy] = this.globalYears[gy].map((y) => y.checked).every((v) => !!v))
        return questionYears
            .reduce((map, { questionId, surveyid_yr }) => {
                const years = surveyid_yr.split(',').map((yearInfo) => {
                    const [surveyId, year] = yearInfo.split('_')
                    return {
                        label: Number(year),
                        value: surveyId,
                        checked: globalChecked[year],
                    }
                }).filter(({ label }) => globalYearStrings.includes(label))

                return Object.assign(map, {
                    [questionId]: R.sortWith(
                        [R.descend(R.prop('label'))],
                        years,
                    ),
                })
            }, {})
    }

    getQuestionBenchmarks = (questionId) => {
        if (questionId === '00000000-0000-0000-0000-000000000000') {
            return this.globalBenchmarks.map((b) => cloneDeep(b))
        }
        const returnedBenchmarks = new Set(
            Object.keys(R.pathOr({}, [questionId], this.questionBenchmarks?.benchmarks)),
        )

        return this.globalBenchmarks
            .filter(({ value }) => returnedBenchmarks.has(value))
            .map((b) => cloneDeep(b))
    }

    handleDerivedSelection = (qid, axis) => {
        this.derivedChartModalSelections[axis] = qid
    }

    handleToggleComparison = (y) => {
        const questionYearsComp = ['LMX', 'OSS'].includes(this.activeChart?.kpiType)
            ? this.globalYears[String(y)] || []
            : this.questionYears[this.activeChart?.questionId].filter((qy) => qy.label === y)
        if (this.activeChart.comparisonYears.includes(y)) {
            questionYearsComp.forEach((q) => this.activeChart.removeComparison(q.value, y))
        } else {
            questionYearsComp.forEach((q) => this.activeChart.addComparison(q.value, y))
        }
    }

    handleToggleBenchmark = (benchmark) => {
        if (this.activeChart.benchmark.includes(benchmark)) {
            this.activeChart.removeBenchmark(benchmark)
        } else {
            this.activeChart.addBenchmark(benchmark)
        }
    }

    get benchmarkDataForChart() {
        const dataKey = this.activeChart?.kpiType === 'LMX'
            ? `${this.activeChart.surveyId}-LMX`
            : this.activeChart?.questionId
        const chartData = this.chartData.get(dataKey)
        if (!chartData) return []

        let baseData = chartData?.data.filter((d) => !d.benchmarkName)

        baseData = baseData.filter((d) => this.activeChart?.comparisonYears.includes(d.year)
            || d.year >= this.activeChart?.year)

        const qbench = this.questionBenchmarks?.benchmarks[this.activeChart?.questionId]

        if (!qbench || !this.activeChart?.benchmark?.length) return baseData

        const chartbench = this.globalBenchmarks.filter((gb) => this.activeChart?.benchmark.includes(gb.value))

        chartbench.forEach((gb) => {
            if (['NPS', 'pct'].includes(chartData.kpiType) && qbench[gb.value]?.pct) {
                baseData.push({
                    data: qbench[gb.value].pct,
                    benchmarkName: capitalize(gb.value),
                    benchmarkPeriod: this.benchmarkPeriod,
                })
            }
        })

        return baseData
    }

    handleToggleGlobalYear = (e) => {
        Object.keys(this.globalYears).slice().forEach((year) => {
            if (year === e.name) {
                this.globalYears[year].forEach((d) => d.checked = e.value)
            }
        })

        this.presentation.section.forEach((section) => {
            section.slide.forEach((slide) => {
                Array.from(slide.question.values()).forEach((question) => {
                    if (this.questionYears[question.questionId]) {
                        const questionYearsComp = this.questionYears[question.questionId]
                            .filter((qy) => qy.label === Number(e.name))
                        return e.value
                            ? questionYearsComp
                                .forEach((q) => question.addComparison(q.value, Number(e.name)))
                            : questionYearsComp
                                .forEach((q) => question.removeComparison(q.value, Number(e.name)))
                    }
                })
            })
        })
    }

    updateGlobalBenchmark = (e) => {
        this.benchmarkPeriod = e.value
        this.presentation.benchmarkPeriod = this.benchmarkPeriods.find(({ value }) => value === this.benchmarkPeriod)?.period
    }

    handleToggleGlobalBenchmark = (e) => {
        this.globalBenchmarks = this.globalBenchmarks.slice().map((benchmark) => {
            if (benchmark.value === e.name) {
                benchmark.checked = e.value
            }
            return benchmark
        })

        this.presentation.toggleBenchmark(e.name)

        this.presentation.section.forEach((section) => {
            section.slide.forEach((slide) => {
                Array.from(slide.question.values()).forEach((question) => {
                    if (this.getQuestionBenchmarks(question.questionId).length) {
                        this.getQuestionBenchmarks(question.questionId).map((b) => {
                            if (b.checked) {
                                question.addBenchmark(b.value)
                            } else {
                                question.removeBenchmark(b.value)
                            }
                        })
                    }
                })
            })
        })
    }

    handleAddAllQuestions = async () => {
        this.questions.forEach((question) => {
            const {
                chart, sorting, benchmark, comparison, comparisonYears, hasNonNumericValues, hasNumericCodes, removeZero, locked, hasOtherOrExclusive
            } = this.mapQuestionToChartDefaults(question)
            this.activeSection.addSlide({
                template: chart === 'verbatim' ? chart : '1x1',
                title: '',
            }, this.currentSlide, 'below')
            this.activeSection.slide[this.currentSlide + 1].addQuestion(0, {
                questionId: question.question.id,
                surveyId: this.appStore.survey.id,
                questionShortText: this.getTitle(question.question),
                questionLongText: this.getQuestionLongText(question.question),
                respondentType: this.survey.respondentType,
                chart: chart ?? 'standardColumn',
                benchmark,
                comparison,
                comparisonYears,
                hasNonNumericValues,
                hasOtherOrExclusive,
                hasNumericCodes,
                year: this.survey.year,
                filter: null,
                settings: { ...settingsDefaults, sorting, removeZero, locked },
                position: 0,
                ppStore: this,
                store: this.appStore,
                questionTypeCode: question.question.questionTypeCode,
                kpiType: question.question.metadata.kpiType,
            })
        })
        await this.getQuestionData(this.questions)
        this.populatePresentationSurveyIds()
        this.changeCurrentSlide(this.currentSection + 1, this.currentSlide + 1)
        this.changeCurrentSlide(0, 0)
    }

    handleSelectInheritedChild = (e) => {
        const child = this.questions.find((q) => q.question.id === e.value)
        const parent = this.questions.find((q) => q.pageQuestionId === child.inheritedPageQuestionId.toUpperCase())

        this.inheritedChartModalSelections = {
            ...this.inheritedChartModalSelections,
            parent: parent.question.id,
            child: child.question.id,
        }
    }

    handleOpenGlobalSettingsModal = () => {
        this.globalSettingsModalOpen = true
    }

    handleCloseGlobalSettingsModal = () => {
        this.globalSettingsModalOpen = false
    }

    handleOpenSlideSettingsModal = () => {
        this.slideSettingsModalOpen = true
    }

    handleCloseSlideSettingsModal = () => {
        this.slideSettingsModalOpen = false
    }

    handleCloseSlideSelectModal = () => {
        this.slideSelectModalOpen = false
        this.addPosition = null
    }

    handleOpenChartSettingsModal = () => {
        console.log('handleOpenChartSettingsModal')
        this.chartSettingsModalOpen = true
    }

    handleCloseChartSettingsModal = () => {
        this.currentChart = null
        this.chartSettingsModalOpen = false
    }

    handleOpenSlideSelectModal = (pos) => {
        if (pos === 'above' && !this.currentSection && !this.currentSlide) return
        this.slideSelectModalOpen = true
        this.addPosition = pos
    }

    handleOpenCrossbreakSettingsModal = () => {
        this.crossbreakSettingsModalVisible = true
    }

    handleCloseCrossbreakSettingsModal = () => {
        this.crossbreakSettingsModalVisible = false
        this.crossbreakChartModalSelections = {
            questions: [
                {
                    question: '',
                    ranking: 'noRanking',
                    base: 0,
                },
            ],
            kpi: '',
            benchmarks: [],
        }
        this.editing = false
    }

    handleOpenFilterModal = () => {
        this.filterModalOpen = true
    }

    handleCloseFilterModal = () => {
        this.filterModalOpen = false
    }

    handleOpenInheritedSettingsModal = () => {
        this.inheritedSettingsModalVisible = true
    }

    handleCloseInheritedSettingsModal = () => {
        this.inheritedSettingsModalVisible = false
        this.inheritedChartModalSelections = {
            parent: '',
            child: '',
            ranking: '',
            base: 0,
            average: 'average',
        }
        this.editing = false
    }

    handleOpenVerbatimSettingsModal = (position, edit = false, slots = 3, chartSlot = 0) => {
        this.currentChart = chartSlot
        this.verbatimPosition = position
        this.verbatimSlots = slots

        if (edit) {
            const pptQuestion = this.activeSection.slide[this.currentSlide].getQuestionAtPosition(chartSlot)
            const category = pptQuestion.chosenCategories.get(position)
            pptQuestion.pendingChanges = {
                category: category?.category ?? '',
                labels: category?.labels ?? [],
            }
        }

        this.verbatimSettingsModalVisible = true
    }

    handleCloseVerbatimSettingsModal = () => {
        this.currentChart = null
        this.verbatimSettingsModalVisible = false
        this.verbatimPosition = 0
        this.verbatimSlots = 0
    }

    handleOpenDerivedChartModal = () => {
        this.derivedChartModalOpen = true
    }

    handleCloseDerivedChartModal = () => {
        this.derivedChartModalSelections = {
            question: '',
            kpi: '',
            base: 0,
        }
        this.derivedChartModalOpen = false
        this.editing = false
    }

    handleEditDerived = () => {
        const question = this.activeSlide.question.get(0)
        this.derivedChartModalSelections = {
            question: question?.questionId ?? '',
            kpi: question?.kpiQuestionId ?? '',
            base: question?.settings.removeBaseLimit ?? 0,
        }
        this.editing = true
        this.handleOpenDerivedChartModal()
    }

    handleEditCrossbreak = () => {
        const question = this.activeSlide.question.get(0)
        this.crossbreakChartModalSelections = {
            questions: question.questions.length
                ? question.questions.map((q) => ({
                    question: q.questionId ?? '',
                    ranking: q.ranking ?? 'noRanking',
                    base: q.base ?? 0,
                }))
                : [
                    {
                        question: '',
                        ranking: 'noRanking',
                        base: 0,
                    },
                ],
            kpi: question.questionId ?? '',
            benchmarks: [],
        }
        this.editing = true
        this.handleOpenCrossbreakSettingsModal()
    }

    handleEditInherited = () => {
        const question = this.activeSlide.question.get(0)
        this.inheritedChartModalSelections = {
            parent: question.questionId ?? '',
            child: R.pathOr('', [0, 'questionId'], question.questions) ?? '',
            ranking: question.settings.sorting,
            base: question.settings.removeBaseLimit,
            average: 'average',
            removeZero: question.settings.removeZero,
        }
        this.editing = true
        this.handleOpenInheritedSettingsModal()
    }

    handleCrossbreakKpiSelection = (e) => {
        this.crossbreakChartModalSelections.kpi = e.value
    }

    handleCrossbreakSettingChange = (i, e) => {
        this.crossbreakChartModalSelections.questions[i][e.name] = e.value
    }

    handleAddCrossbreakSlide = async () => {
        const questions = this.crossbreakChartModalSelections.questions
            .map((q) => this.questions.find((qu) => qu.question.id === q.question))
        const kpi = this.questions.find((q) => q.question.id === this.crossbreakChartModalSelections.kpi)

        if (!this.editing) {
            this.activeSection.addSlide({
                template: 'crosstab',
                title: '',
            }, this.currentSlide, this.addPosition)
        }

        const {
            benchmark, comparison,
        } = this.mapQuestionToChartDefaults(kpi)

        const slidePos = (this.editing || this.addPosition === 'above') ? this.currentSlide : this.currentSlide + 1

        this.activeSection.slide[slidePos].addQuestion(0, {
            surveyId: this.appStore.survey.id,
            questionId: kpi.question.id,
            questionShortText: `${this.getTitle(kpi.question)}${questions.map((q) => ` vs ${this.getTitle(q.question)}`).join('')}`,
            questionLongText: `${this.getQuestionLongText(kpi.question)} vs questions`,
            questions: questions.map((q, i) => ({
                questionId: q.question.id,
                ranking: this.crossbreakChartModalSelections.questions[i].ranking,
                removeBaseLimit: this.crossbreakChartModalSelections.questions[i].base,
            })),
            respondentType: this.survey.respondentType,
            chart: 'bar',
            year: this.survey.year,
            benchmark,
            comparison,
            filter: null,
            settings: { ...settingsDefaults },
            position: 0,
            ppStore: this,
            store: this.appStore,
        })

        await this.getCrossTabChartData(this.activeSection.slide[slidePos].getQuestionAtPosition(0))

        if (!this.editing) {
            this.changeCurrentSlide(this.currentSection, this.currentSlide + 1)

            if (this.addPosition === 'above') {
                this.changeCurrentSlide(this.currentSection, this.currentSlide)
            }
        }

        this.populatePresentationSurveyIds()
        this.addPosition = null
        this.handleCloseCrossbreakSettingsModal()
    }

    handleAddDerivedSlide = async () => {
        const question = this.questions.find((q) => q.question.id === this.derivedChartModalSelections.question)
        const kpi = this.questions.find((q) => q.question.id === this.derivedChartModalSelections.kpi)
        if (!this.editing) {
            this.activeSection.addSlide({
                template: 'derived',
                title: '',
            }, this.currentSlide, this.addPosition)
        }

        const slidePos = (this.editing || this.addPosition === 'above') ? this.currentSlide : this.currentSlide + 1

        const ppQuestion = {
            questionId: question.question.id,
            questionShortText: this.getTitle(question.question),
            questionLongText: this.getQuestionLongText(question.question),
            kpiQuestionId: kpi.question.id,
            kpiQuestionShortText: this.getTitle(kpi.question),
            kpiQuestionLongText: this.getQuestionLongText(kpi.question),
            surveyId: this.survey.surveyId,
            respondentType: this.survey.respondentType,
            chart: 'quadrantAnalysis',
            filter: null,
            settings: { ...settingsDefaults, removeBaseLimit: parseInt(this.derivedChartModalSelections.base, 10) },
            position: 0,
            year: this.survey.year,
            ppStore: this,
            store: this.appStore,
        }

        this.activeSection.slide[slidePos].addQuestion(0, ppQuestion)

        if (!this.editing) {
            this.changeCurrentSlide(this.currentSection, this.currentSlide + 1)

            if (this.addPosition === 'above') {
                this.changeCurrentSlide(this.currentSection, this.currentSlide)
            }
        }

        this.populatePresentationSurveyIds()
        await this.getDerivedChartData(ppQuestion)
        this.addPosition = null
        this.handleCloseDerivedChartModal()
    }

    handleAddInheritedSlide = async () => {
        const parent = this.questions.find((q) => q.question.id === this.inheritedChartModalSelections.parent)
        const child = this.questions.find((q) => q.question.id === this.inheritedChartModalSelections.child)

        if (!this.editing) {
            this.activeSection.addSlide({
                template: 'inherited',
                title: '',
            }, this.currentSlide, this.addPosition)
        }

        const slidePos = (this.editing || this.addPosition === 'above') ? this.currentSlide : this.currentSlide + 1

        this.activeSection.slide[slidePos].addQuestion(0, {
            questionId: parent.question.id,
            surveyId: this.appStore.survey.id,
            questionShortText: `${this.getTitle(parent.question)} / ${this.getTitle(child.question)}`,
            questionLongText: `${this.getQuestionLongText(parent.question)} / ${this.getQuestionLongText(child.question)}`,
            respondentType: this.survey.respondentType,
            questions: [{ questionId: child.question.id, ranking: 'noRanking', removeBaseLimit: parseInt(this.inheritedChartModalSelections.base, 10), removeZero: this.inheritedChartModalSelections.removeZero }],
            chart: 'standardBar',
            benchmark: [],
            comparison: [],
            filter: null,
            settings: { ...settingsDefaults, sorting: this.inheritedChartModalSelections.ranking, removeBaseLimit: parseInt(this.inheritedChartModalSelections.base, 10), removeZero: this.inheritedChartModalSelections.removeZero },
            position: 0,
            ppStore: this,
            store: this.appStore,
        })
        await this.getInheritedChartData(parent, child)

        if (!this.editing) {
            this.changeCurrentSlide(this.currentSection, this.currentSlide + 1)

            if (this.addPosition === 'above') {
                this.changeCurrentSlide(this.currentSection, this.currentSlide)
            }
        }

        this.populatePresentationSurveyIds()
        this.addPosition = null
        this.handleCloseInheritedSettingsModal()
    }

    updateInheritedSettings = (e) => {
        this.inheritedChartModalSelections[e.name] = e.value
    }

    editSlideTitle = (e) => {
        this.activeSlide.title = e.value
    }

    handleSearch = (e) => {
        this.search = e.value
        this.filterQuestions()
    }

    removeQuestionData = (qid) => {
        this.questionData.delete(qid)
    }

    onQuestionDragEnd = async (result) => {
        const destinationDropId = R.pathOr('', ['destination', 'droppableId'], result)

        const q = this.questions.find(
            (qu) => String(qu.question.id) === String(result.draggableId),
        )
        if (!q) return

        const {
            chart, sorting, benchmark, comparison, comparisonYears, hasNonNumericValues, hasNumericCodes, removeZero, locked, hasOtherOrExclusive
        } = this.mapQuestionToChartDefaults(q)

        if (destinationDropId === 'add-above') {
            if (!this.currentSection && !this.currentSlide) return

            this.activeSection.addSlide(
                {
                    template: ['TX', 'TA'].includes(q.question.questionTypeCode) ? 'verbatim' : '1x1',
                    title: '',
                },
                this.currentSlide,
                'above',
            )
            this.activeSection.slide[this.currentSlide].addQuestion(0, {
                questionId: q.question.id,
                questionShortText: this.getTitle(q.question),
                questionLongText: this.getQuestionLongText(q.question),
                respondentType: this.survey.respondentType,
                surveyId: this.survey.surveyId,
                chart,
                benchmark,
                comparison,
                comparisonYears,
                hasNonNumericValues,
                hasOtherOrExclusive,
                hasNumericCodes,
                year: this.survey.year,
                filter: null,
                settings: {
                    ...settingsDefaults, sorting, removeZero, locked,
                },
                position: 0,
                ppStore: this,
                store: this.appStore,
                questionTypeCode: q.question.questionTypeCode,
                kpiType: q.question.metadata.kpiType,
            })
            this.populatePresentationSurveyIds()
            this.changeCurrentSlide(this.currentSection, this.currentSlide + 1)
            this.changeCurrentSlide(this.currentSection, this.currentSlide - 1)
            const pptQuestion = this.activeSection.slide[this.currentSlide].getQuestionAtPosition(0)

            if (['TX', 'TA'].includes(q.question.questionTypeCode)) {
                await pptQuestion.fetchData()
            } else {
                await this.getQuestionData([q])
            }

            return
        }

        if (destinationDropId === 'add-below') {
            this.activeSection.addSlide(
                {
                    template: ['TX', 'TA'].includes(q.question.questionTypeCode) ? 'verbatim' : '1x1',
                    title: '',
                },
                this.currentSlide,
                'below',
            )

            this.activeSection.slide[this.currentSlide + 1].addQuestion(0, {
                questionId: q.question.id,
                surveyId: this.survey.surveyId,
                questionShortText: this.getTitle(q.question),
                questionLongText: this.getQuestionLongText(q.question),
                respondentType: this.survey.respondentType,
                chart,
                benchmark,
                comparison,
                comparisonYears,
                hasNonNumericValues,
                hasOtherOrExclusive,
                hasNumericCodes,
                year: this.survey.year,
                filter: null,
                settings: {
                    ...settingsDefaults, sorting, removeZero, locked,
                },
                position: 0,
                ppStore: this,
                store: this.appStore,
                questionTypeCode: q.question.questionTypeCode,
                kpiType: q.question.metadata.kpiType,
            })
            this.populatePresentationSurveyIds()
            this.changeCurrentSlide(this.currentSection, this.currentSlide + 1)

            const pptQuestion = this.activeSection.slide[this.currentSlide].getQuestionAtPosition(0)

            if (pptQuestion.isVerbatim) {
                await pptQuestion.fetchData()
            } else {
                await this.getQuestionData([q])
            }

            return
        }

        if (!destinationDropId.includes('slide-')) return

        const index = parseInt(destinationDropId.split('slide-')[1], 10)

        if (this.activeSlide.template === '1x2'
            && index !== 1 && ['TX', 'TA'].includes(q.question.questionTypeCode)) return
        if (this.activeSlide.template !== '1x2' && ['TX', 'TA'].includes(q.question.questionTypeCode)) return

        const showAverage = this.activeSlide.template === '2x2' && ['RD', 'SD'].includes(q.question.questionTypeCode)

        this.activeSlide.addQuestion(index, {
            questionId: q.question.id,
            questionShortText: this.getTitle(q.question),
            questionLongText: this.getQuestionLongText(q.question),
            respondentType: this.survey.respondentType,
            surveyId: this.survey.surveyId,
            chart,
            benchmark,
            comparison,
            comparisonYears,
            hasNonNumericValues,
            hasOtherOrExclusive,
            hasNumericCodes,
            year: this.survey.year,
            filter: null,
            settings: {
                ...settingsDefaults, sorting, removeZero, locked, showAverage,
            },
            position: index,
            ppStore: this,
            store: this.appStore,
            questionTypeCode: q.question.questionTypeCode,
            kpiType: q.question.metadata.kpiType,
        })
        this.populatePresentationSurveyIds()
        const pptQuestion = this.activeSection.slide[this.currentSlide].getQuestionAtPosition(index)

        if (['TX', 'TA'].includes(q.question.questionTypeCode)) {
            await pptQuestion.fetchData()
        } else {
            await this.getQuestionData([q])
        }
    }

    populatePresentationSurveyIds = () => {
        const surveyIds = new Set()

        this.presentation.section
            .forEach((s) => s.slide
                .forEach((sl) => sl.question
                    .forEach((q) => surveyIds.add(q.surveyId))))

        this.presentation.surveyId = Array.from(surveyIds)
    }

    onSlideDragEnd = (result) => {
        const destinationDropId = R.pathOr('', ['destination', 'droppableId'], result)
        const sourceDropId = R.pathOr('', ['source', 'droppableId'], result)
        if (destinationDropId.includes('slide-list-section-') && sourceDropId.includes('slide-list-section-')) {
            const destinationSection = parseInt(destinationDropId.split('slide-list-section-')[1], 10)
            const sourceSection = parseInt(sourceDropId.split('slide-list-section-')[1], 10)
            if (destinationSection === sourceSection) {
                this.presentation.section[destinationSection].moveSlide(result.source.index, result.destination.index)

                if (this.currentSlide === result.source.index) {
                    this.changeCurrentSlide(this.currentSection, result.destination.index)
                }
            } else {
                const slideToMove = this.presentation.section[sourceSection].slide[result.source.index]
                this.presentation.section[sourceSection].deleteSlide(result.source.index)
                this.presentation.section[destinationSection].addSlide(slideToMove, result.destination.index, 'below')

                if (!this.presentation.section[sourceSection].slide.length) {
                    if (this.currentSection === sourceSection) {
                        this.changeCurrentSlide(0, 0)
                    }

                    this.presentation.removeSection(sourceSection)
                    this.changeCurrentSlide(0, 0)
                }
            }
        }
    }

    addQuestionToCrossbreak = () => {
        this.crossbreakChartModalSelections.questions.push({
            question: '',
            ranking: 'noRanking',
            base: 0,
        })
    }

    handleRemoveQuestion = (pos) => {
        this.activeSlide.removeQuestion(pos)
        this.populatePresentationSurveyIds()
    }

    removeQuestionFromCrossbreak = (index) => {
        this.crossbreakChartModalSelections.questions = this.crossbreakChartModalSelections.questions
            .filter((q, i) => i !== index)
    }

    filterQuestions = () => {
        this.filteredQuestions = this.questions
            .filter((qu) => this.getTitle(qu.question).toLocaleLowerCase().includes(this.search.toLocaleLowerCase()))
    }

    toggleLayoutOpen = () => {
        this.slideLayoutOpen = !this.slideLayoutOpen
    }

    handleKeyboardListener = (dir) => {
        if (dir < 0) {
            if (this.currentSection === 0 && this.currentSlide === 0) return null
            if (this.currentSection === 0 && this.currentSlide !== 0) {
                return this.changeCurrentSlide(0, this.currentSlide - 1)
            }
            if (this.currentSection !== 0 && this.currentSlide === 0) {
                return this.changeCurrentSlide(
                    this.currentSection - 1,
                    this.presentation.section[this.currentSection - 1].slide.length - 1,
                )
            }
            return this.changeCurrentSlide(this.currentSection, this.currentSlide - 1)
        }
        if (dir > 0) {
            if (this.currentSection === (this.presentation.section.length - 1)
                && this.currentSlide === (this.presentation.section[this.currentSection].slide.length - 1)) return null

            if (this.currentSlide === (this.presentation.section[this.currentSection].slide.length - 1)) {
                return this.changeCurrentSlide(this.currentSection + 1, 0)
            }

            return this.changeCurrentSlide(this.currentSection, this.currentSlide + 1)
        }
    }

    changeCurrentSlide = (section, slide) => {
        if (this.currentSection !== section) this.currentSection = section
        this.currentSlide = slide
        this.filterQuestions()
    }

    handleSelectSurvey = async (e) => {
        this.survey = this.surveys.find((surv) => surv.surveyId === e.value)
        this.filteredQuestions = this.questions
        await this.getMergefielsdForSurvey()
        await this.getQuestionsForSurvey()
    }

    handleCloseSideControls = () => {
        this.sideNavigationOpen = ''
    }

    handleEditChart = (i) => {
        this.currentChart = i
        this.search = ''
        this.handleOpenChartSettingsModal()
    }

    getTagBenchmarks = (benchmarkTags) => {
        const { langcode } = this.appStore
        return benchmarkTags.flatMap(({
            customTagName, customTagId, customTagConfig, tagOptions, parentConfig,
        }) => {
            const config = parentConfig || customTagConfig
            const scopes = JSON.parse(config).benchmarkOptions.filter((scope) => scope !== 'publish')

            return scopes.flatMap((scope) => (
                tagOptions.map(({ tagOptionId, tagOptionConfig }) => {
                    const parsedTagConfig = JSON.parse(tagOptionConfig)
                    const tagName = R.pathOr(
                        R.pathOr(
                            R.pathOr('', ['langs', 'en_GB'], parsedTagConfig),
                            ['langs', langcode],
                            parsedTagConfig,
                        ),
                        ['name'], parsedTagConfig,
                    )
                    return {
                        tagOptionId,
                        customTag: customTagId,
                        customTagName,
                        scope,
                        shortLabel: tagName,
                        value: `${scope}_${tagOptionId}`,
                        label: `${scope} (${customTagName} - ${tagName})`,
                    }
                })
            ))
        })
    }

    handleAddLMXQuestion = async () => {
        const { appStore } = this

        this.activeSection.addSlide({
            template: 'lmx', title: 'Satisfaction vs Loyalty',
        }, this.currentSlide, this.addPosition)

        const comparisonYears = new Set()
        const comparison = new Set()

        Object.entries(this.globalYears).forEach(([year, data]) => {
            if (data.map((d) => d.checked).every((v) => !!v) && year <= this.survey.year) {
                comparisonYears.add(Number(year))
                data.forEach((d) => comparison.add(d.value))
            }
        })

        const benchmark = this.globalBenchmarks
            .filter((b) => b.checked)
            .map((b) => b.value)

        this.activeSection.slide[this.currentSlide + 1].addQuestion(0, {
            questionId: '00000000-0000-0000-0000-000000000000',
            surveyId: this.survey.surveyId,
            questionShortText: '',
            questionLongText: '',
            respondentType: this.survey.respondentType,
            chart: 'lmx',
            benchmark,
            comparison: Array.from(comparison),
            comparisonYears: Array.from(comparisonYears),
            hasNonNumericValues: false,
            hasOtherOrExclusive: false,
            hasNumericCodes: false,
            year: this.survey.year,
            filter: null,
            settings: { ...settingsDefaults, sorting: 'none', removeZero: false, locked: false },
            position: 0,
            ppStore: this,
            store: this.appStore,
            questionTypeCode: '',
            kpiType: 'LMX',
        })

        const reqData = []
        const groupId = uuidv4()

        reqData.push({
            benchmarkType: 'YOY',
            groupId,
            industry: this.survey.industry,
            productId: this.survey.productId,
            productType: this.survey.producttypecode,
            respondentType: this.survey.respondentType,
            responseType: 'LMX',
            scope: 'survey',
            surveyType: this.survey.surveyType,
            questionId: '00000000-0000-0000-0000-000000000000',
        })

        const resp = await appStore.http.post('/report/v1/dashboard', JSON.stringify({
            widget: reqData,
        }))

        if (!resp) return
        const [qdata] = resp.widget.filter((w) => w.groupId === groupId)

        this.questionData.set(`${this.survey.surveyId}-LMX`, qdata)

        const d = {
            ...this.questionData.get(`${this.survey.surveyId}-LMX`),
            dataDetails: {
                type: 'pct',
            },
            otherOrExclusiveLabels: [],
            columns: [],
            responses: [],
            min: 1,
            max: 100,
            averages: [],
            percentiles: undefined,
            isCurrency: false,
            quadrantCurrency: undefined,
            nonQuestionType: 'KPI',
            questionInitialLocale: '',
            questionTypeCode: '',
            questionOptions: [],
            kpiType: '',
            benchmarkType: '',
            baseSizes: [],
            surveyYear: this.survey.year,
            widgetRenderSettings: {
                showLegend: true,
                showDataLabels: true,
                labelPosition: this.defaultLabelPositions.default,
                showOptionLabels: false,
                rotateXAxisLabels: false,
                orientation: 'horizontal',
                swapSeriesXAxis: false,
                format: 'numeric',
                sortBy: 'none',
                sortDirection: -1,
                removeZeroNull: 'dontRemove',
                dataDecimals: 0,
                showTitle: false,
                showYAxis: false,
                scrollable: false,
            },
        }

        this.chartData.set(`${this.survey.surveyId}-LMX`, d)
        this.changeCurrentSlide(this.currentSection, this.currentSlide + 1)
        this.handleCloseSlideSelectModal()
    }

    handleAddOssQuestion = async () => {
        const { appStore } = this

        this.activeSection.addSlide({
            template: 'oss', title: 'Overall Show Score',
        }, this.currentSlide, this.addPosition)

        const comparisonYears = new Set()
        const comparison = new Set()

        Object.entries(this.globalYears).forEach(([year, data]) => {
            if (data.map((d) => d.checked).every((v) => !!v) && year <= this.survey.year) {
                comparisonYears.add(Number(year))
                data.forEach((d) => comparison.add(d.value))
            }
        })

        const benchmark = this.globalBenchmarks
            .filter((b) => b.checked)
            .map((b) => b.value)

        this.activeSection.slide[this.currentSlide + 1].addQuestion(0, {
            questionId: '00000000-0000-0000-0000-000000000000',
            surveyId: this.survey.surveyId,
            questionShortText: '',
            questionLongText: '',
            respondentType: this.survey.respondentType,
            chart: 'standardColumn',
            benchmark,
            comparison: Array.from(comparison),
            comparisonYears: Array.from(comparisonYears),
            hasNonNumericValues: false,
            hasOtherOrExclusive: false,
            hasNumericCodes: false,
            year: this.survey.year,
            filter: null,
            settings: { ...settingsDefaults, sorting: 'none', removeZero: false, locked: false },
            position: 0,
            ppStore: this,
            store: this.appStore,
            questionTypeCode: '',
            kpiType: 'OSS',
        })

        const reqData = []
        const groupId = uuidv4()

        reqData.push({
            benchmarkType: 'YOY',
            groupId,
            industry: this.survey.industry,
            productId: this.survey.productId,
            productType: this.survey.producttypecode,
            respondentType: this.survey.respondentType,
            responseType: 'OSS',
            scope: 'survey',
            surveyType: this.survey.surveyType,
            questionId: '00000000-0000-0000-0000-000000000000',
        })

        const resp = await appStore.http.post('/report/v1/dashboard', JSON.stringify({
            widget: reqData,
        }))

        if (!resp) return
        const [qdata] = resp.widget.filter((w) => w.groupId === groupId)

        this.questionData.set(`${this.survey.surveyId}-OSS`, qdata)

        const d = {
            ...this.questionData.get(`${this.survey.surveyId}-OSS`),
            dataDetails: {
                type: 'OSS',
            },
            options: [],
            otherOrExclusiveLabels: [],
            columns: [],
            responses: [],
            min: 1,
            max: 100,
            averages: [],
            percentiles: undefined,
            isCurrency: false,
            quadrantCurrency: undefined,
            nonQuestionType: 'KPI',
            questionInitialLocale: '',
            questionTypeCode: '',
            questionOptions: [],
            kpiType: '',
            benchmarkType: '',
            baseSizes: [],
            surveyYear: this.survey.year,
            widgetRenderSettings: {
                showLegend: true,
                showDataLabels: true,
                labelPosition: this.defaultLabelPositions.default,
                showOptionLabels: false,
                rotateXAxisLabels: false,
                orientation: 'horizontal',
                swapSeriesXAxis: false,
                format: 'numeric',
                sortBy: 'none',
                sortDirection: -1,
                removeZeroNull: 'dontRemove',
                dataDecimals: 0,
                showTitle: false,
                showYAxis: false,
                scrollable: false,
            },
        }

        this.chartData.set(`${this.survey.surveyId}-OSS`, d)
        this.changeCurrentSlide(this.currentSection, this.currentSlide + 1)
        this.handleCloseSlideSelectModal()
    }

    compareValues = (value1, value2) => {
        const v1 = value1 ? Number(value1) : 0
        const v2 = value2 ? Number(value2) : 0
        if (v2 > v1) {
            return { className: 'gt', icon: '↗' }
        }
        if (v2 < v1) {
            return { className: 'lt', icon: '↘' }
        }
        if (v2 === v1) {
            return { className: 'eq', icon: '─' }
        }
        return { className: '', icon: '' }
    }

    handleAddSlide = async (template) => {
        if (template === 'derived') {
            this.handleOpenDerivedChartModal()
        } else if (template === 'inherited') {
            this.handleOpenInheritedSettingsModal()
        } else if (template === 'crosstab') {
            this.handleOpenCrossbreakSettingsModal()
        } else if (template === 'oss') {
            await this.handleAddOssQuestion()
        } else if (template === 'lmx') {
            await this.handleAddLMXQuestion()
        } else if (template === 'sectionHeader') {
            this.presentation.addSection({
                slide: [
                    {
                        template, title: '',
                    },
                ],
            }, this.currentSection, this.addPosition)

            this.changeCurrentSlide(this.currentSection + 1, 0)

            if (this.addPosition === 'above') {
                this.changeCurrentSlide(this.currentSection - 1, 0)
            }
        } else {
            const title = ['swot', 'summary'].includes(template) ? 'Summary' : ''
            this.activeSection.addSlide({
                template, title,
            }, this.currentSlide, this.addPosition)

            this.changeCurrentSlide(this.currentSection, this.currentSlide + 1)

            if (this.addPosition === 'above') {
                this.changeCurrentSlide(this.currentSection, this.currentSlide - 1)
            }
        }

        this.handleCloseSlideSelectModal()
    }

    deleteSlide = () => {
        if (this.currentSection === 0
                && this.presentation.section.length === 1
                && this.activeSection.slide.length === 1) return

        const slideToDelete = this.currentSlide

        // if this is the only slide in the current section
        if (this.activeSection.slide.length === 1) {
            const sectionToDelete = this.currentSection
            this.changeCurrentSlide(this.currentSection - 1,
                this.presentation.section[this.currentSection].slide.length - 1)
            this.presentation.removeSection(sectionToDelete)
            return
        }

        if (this.activeSection.slide[this.currentSlide - 1]) {
            this.changeCurrentSlide(this.currentSection, this.currentSlide - 1)
        }

        if (this.currentSlide === 0) {
            this.changeCurrentSlide(this.currentSection, 0)
        }

        this.activeSection.deleteSlide(slideToDelete)
    }

    getTitle = (question) => {
        const { appStore } = this
        const { userData } = appStore
        const { locale } = userData

        const initialLocale = R.pathOr('en_GB', ['metadata', 'initialLocale'], question)
        const questionContent = R.pathOr({}, ['content', 'shortTitle'], question)
        const title = R.pathOr('', ['text'], getQuestionTitle(
            {
                content: questionContent,
                locale,
                defaultLocale: initialLocale,
                initialLocale: appStore.survey.metadata.defaultLocale,
            },
        ))

        return MergeFields.replace(title, initialLocale, this.mergefields)
    }

    getQuestionLongText = (question) => {
        const { appStore } = this
        const { userData } = appStore
        const { locale } = userData

        const initialLocale = R.pathOr('en_GB', ['metadata', 'initialLocale'], question)
        const questionContent = R.pathOr({}, ['content', 'longTitle'], question)
        const longText = R.pathOr('', ['text'], getQuestionTitle(
            {
                content: questionContent,
                locale,
                defaultLocale: initialLocale,
                initialLocale: appStore.survey.metadata.defaultLocale,
            },
        ))

        return MergeFields.replace(longText, initialLocale, this.mergefields)
    }

    getMergefielsdForSurvey = async () => {
        this.mergefields = await this.appStore.http.get(`/mergefields/survey/${this.survey.surveyId}`)
    }

    getQuestionsForSurvey = async () => {
        const questions = await this.appStore.http.get(`/api/survey/${this.survey.surveyId}/questions`)
        this.questions = questions.filter((q) => !['I', 'DT', 'TL', 'UV', 'ST', 'SG'].includes(q.question.questionTypeCode))
        this.filteredQuestions = this.questions
    }

    mapQuestionToChartDefaults = (q) => {
        const { question, question: { questionTypeCode } } = q
        let hasNumericCodes = false
        let hasOtherOrExclusive = false
        if (questionTypeCode === 'RD') {
            hasNumericCodes = !!(question.optionSet.hasNumericCodes)
            hasOtherOrExclusive = question.optionSet.options.some(({ hasOptionOther }) => hasOptionOther)
            if (!isEmpty(question.metadata.kpiType)) {
                const { kpiType: questionKpiType } = question.metadata
                this.kpis[questionKpiType] = true
            }
        }

        if (questionTypeCode === 'CH') {
            hasOtherOrExclusive = question.optionSet.options.some(({ hasOptionOther, isExclusive }) => hasOptionOther || isExclusive)
        }

        let hasNonNumericValues = false
        if (questionTypeCode === 'TB') {
            hasNumericCodes = !!(question.optionSetX?.hasNumericCodes)
            const nonNumericOptions = (ele) => !ele.hasOwnProperty('reportingValue')
            hasNonNumericValues = question.optionSetX.options.some(nonNumericOptions)
        }

        const defaults = this.getChartDefaults(question, hasNumericCodes, hasOtherOrExclusive)

        const comparison = new Set()
        const comparisonYears = new Set()
        const benchmark = this.getQuestionBenchmarks(question.id).filter((b) => b.checked).map((b) => b.value)
        if (this.questionYears[question.id]) {
            Object.entries(this.globalYears).forEach(([year, data]) => {
                if (data.map((d) => d.checked).every((v) => !!v)) {
                    const globalSurveyIds = data.map((d) => d.value)
                    this.questionYears[question.id].forEach((qy) => {
                        if (globalSurveyIds.includes(qy.value)) {
                            comparison.add(qy.value)
                        }
                    })
                    comparisonYears.add(Number(year))
                }
            })
        }
        return {
            chart: defaults.chartType,
            sorting: defaults.sorting,
            removeZero: defaults.removeZero ?? false,
            locked: defaults.lock ?? false,
            benchmark,
            hasNumericCodes,
            hasOtherOrExclusive,
            hasNonNumericValues,
            comparison: Array.from(comparison),
            comparisonYears: Array.from(comparisonYears),
        }
    }

    formatData = (data, element, surveyYear) => {
        if (!data || !element.dataDetails) return {}
        // if (!data) return this.dataFormatter?.formatData([], element)
        const productFilters = element.scope === 'product' ? R.pathOr([], ['filter'], element).map((f) => f.productId) : []
        const portfolioFilters = element.scope === 'portfolio' ? R.pathOr([], ['filter'], element).map((f) => f.portfolioId) : []
        const divisionFilters = element.scope === 'division' ? R.pathOr([], ['filter'], element).map((f) => f.divisionId) : []
        const allowedBenchmarks = !['treemap', 'pie', 'donut'].includes(element.chartType)
        const elementBenchmarks = R.pathOr(this.possibleBenchmarks[6], ['widgetRenderSettings', 'allowedBenchmarks'], element)
        const filteredData = data.filter((d) => (
            this.benchmarkCheck(d, allowedBenchmarks, elementBenchmarks)
            && this.productFilterCheck(d, productFilters)
            && this.portfolioFilterCheck(d, portfolioFilters)
            && this.divisionFilterCheck(d, divisionFilters)
        ))
        return this.dataFormatter?.formatData(filteredData, element, surveyYear)
    }

    benchmarkCheck = (dataObj, allowedBenchmarks, elementBenchmarks) => {
        const { benchmarkPeriod } = this
        return true
        if (!dataObj.benchmark) return true
        if (!allowedBenchmarks) return false
        if (dataObj.benchmarkPeriod !== benchmarkPeriod) return false
        if (!elementBenchmarks.includes(dataObj.benchmark)) return false
        if (dataObj.tagOptionId !== 0) return Boolean(this.benchmarks.get(`${dataObj.benchmark === 'client' ? 'company' : dataObj.benchmark}_${dataObj.tagOptionId}`))
        if (dataObj.termId) return Boolean(this.benchmarks.get('sector'))
            return true
        return this.benchmarks.has(dataObj.benchmark)
            ? this.benchmarks.get(dataObj.benchmark) : true
    }

    productFilterCheck = (dataObj, productFilters) => {
        const { productId, benchmark } = dataObj
        if (!productFilters.length) return true
        return productFilters.includes(productId) || benchmark
    }

    portfolioFilterCheck = (dataObj, portfolioFilters) => {
        const { portfolioId, benchmark } = dataObj
        if (!portfolioFilters.length) return true
        return portfolioFilters.includes(portfolioId) || benchmark
    }

    divisionFilterCheck = (dataObj, divisionFilters) => {
        const { divisionId, benchmark } = dataObj
        if (!divisionFilters.length) return true
        return divisionFilters.includes(divisionId) || benchmark
    }

    getChartScope = (oid) => {
        switch (oid) {
            case 3:
                return 'client'
            case 4:
                return 'division'
            case 5:
                return 'portfolio'
            case 6:
                return 'event'
            default:
                return 'NoOID'
        }
    }

    getChart = (element, data, yearFilters) => {
        let noData = !data
        let formattedData
        const { questionTypeCode, settings } = element

        const survey = this.surveys.find(({ surveyId }) => surveyId.toUpperCase() === element.surveyId.toUpperCase())
        const { sorting, removeZero } = settings

        if (element.chartType !== 'quadrantAnalysis' && !['crossbreak', 'inherited'].includes(element.kpiType)) {
            const yearFilteredData = data?.filter(
                (d) => yearFilters?.includes(d.year) || d.year === element.surveyYear || !!d.benchmarkName,
            )

            formattedData = this.formatData(
                yearFilteredData, element, survey.year,
            )

            if (settings.showAverage && questionTypeCode !== 'TB') {
                formattedData.fData = (element.averages?.fData ?? []).map((d) => ({
                    data: [...d.data],
                    name: d.name,
                })).filter(({ name }) => yearFilters.includes(name) || name === element.surveyYear)
                formattedData.xAxisLabels = (element.averages?.xAxisLabels ?? []).slice()
            }

            if (formattedData.fData) {
                const isStandardBar = element.chartType === 'standardBar'
                if (isStandardBar) {
                    formattedData.fData = formattedData.fData.map((series) => (
                        { ...series, data: series.data.slice().reverse() }
                    ))
                }

                let yearData = (questionTypeCode === 'TB'
                    ? formattedData.fData?.[0]?.data.map(({ name, avg, value: v }) => {
                        let value = avg ?? 0
                        if (settings.showAverage) value = avg ?? v
                        return { name, value }
                    })
                    : formattedData.fData.find(({ name }) => name === survey.year)?.data
                ) ?? []

                if (settings.showAverage && questionTypeCode !== 'TB') {
                    yearData = formattedData.fData.map(({ data: [{ value }], name }) => ({
                        value,
                        name,
                    }))
                }

                let mappedYearData
                switch (sorting) {
                    case 'highToLow':
                    case 'lowToHigh':
                        mappedYearData = yearData.map((d) => d.value)
                        break
                    case 'aToZ':
                    case 'zToA':
                        mappedYearData = yearData.map((d) => d.name)
                        break
                    default:
                        mappedYearData = yearData.map((_, i) => i)
                }

                let indexArray = sortedIndexArray(mappedYearData).filter((i) => (
                    !removeZero || yearData[i].value !== 0
                ))
                if (['highToLow', 'zToA'].includes(sorting)) {
                    indexArray = indexArray.reverse()
                }

                if (indexArray.length > 0 && !settings.showAverage) {
                    if (['TB', 'SG'].includes(questionTypeCode)) {
                        indexArray = indexArray.reverse()
                        formattedData?.fData?.map((fData) => {
                            if (Array.isArray(fData?.data)) {
                                const b = fData.data.slice()
                                fData.data = indexArray.map((i) => b[i])
                            }
                        })
                    } else {
                        formattedData.fData.map((data) => {
                            const b = data.data.slice()
                            data.data = indexArray.map((i) => b[i])
                        })
                    }
                    const labels = formattedData.xAxisLabels.slice()
                    formattedData.xAxisLabels = indexArray.map((i) => MergeFields.replace(labels[i], this.appStore.locale, this.mergefields))
                } else if (settings.showAverage) {
                    const data = formattedData.fData.slice()
                    formattedData.fData = indexArray.map((i) => data[i])
                }

                if (isStandardBar) {
                    formattedData.fData = formattedData.fData.map((series) => (
                        { ...series, data: series.data.slice().reverse() }
                    ))
                }

                if (settings.locked && element.otherOrExclusiveLabels?.size > 0) {
                    if (isStandardBar) {
                        formattedData.fData = formattedData.fData.map((series) => (
                            { ...series, data: series.data.slice().reverse() }
                        ))
                    }

                    const { otherOrExclusiveLabels } = element
                    const firstSeriesData = formattedData.fData[0]?.data ?? []
                    const normalIndices = []
                    const otherIndices = []
                    firstSeriesData.forEach((d, i) => {
                        if (d?.name != null && otherOrExclusiveLabels.has(d.name)) {
                            otherIndices.push(i)
                        } else {
                            normalIndices.push(i)
                        }
                    })
                    if (otherIndices.length > 0) {
                        const reorderedIndices = [...normalIndices, ...otherIndices]
                        formattedData.fData = formattedData.fData.map((series) => {
                            const b = series.data.slice()
                            return { ...series, data: reorderedIndices.map((i) => b[i]) }
                        })
                        const sortedLabels = formattedData.xAxisLabels.slice()
                        formattedData.xAxisLabels = reorderedIndices.map((i) => sortedLabels[i])
                    }

                    if (isStandardBar) {
                        formattedData.fData = formattedData.fData.map((series) => (
                            { ...series, data: series.data.slice().reverse() }
                        ))
                    }
                }
            }
        } else if (element.kpiType === 'crossbreak') {
            formattedData = {
                fData: data,
                lineData: element.lineData,
                xAxisLabels: data[0].data.map((v) => v.name),
            }
        } else if (element.kpiType === 'inherited') {
            let mappedData
            switch (sorting) {
                case 'highToLow':
                case 'lowToHigh':
                    mappedData = data[0].data.map((d) => d.value)
                    break
                case 'aToZ':
                case 'zToA':
                    mappedData = data[0].data.map((d) => d.name)
                    break
                default:
                    mappedData = data[0].data.map((_, i) => i)
            }

            let indexArray = sortedIndexArray(mappedData).filter((i) => (
                !removeZero || data[0].data[i].value !== 0
            ))
            if (['highToLow', 'zToA'].includes(sorting)) {
                indexArray = indexArray.reverse()
            }

            const labels = data[0].data.map((v) => v.name)
            const lineData = { ...element.lineData[0] }
            const sortedData = [...data]
            sortedData[0].data = indexArray.map((i) => data[0].data[i])
            lineData.data = indexArray.map((i) => lineData.data[i])
            formattedData = {
                fData: sortedData,
                lineData: [lineData],
                xAxisLabels: indexArray
                    .map((i) => MergeFields.replace(labels[i], this.appStore.locale, this.mergefields)),
            }
        } else {
            formattedData = {
                fData: data,
                xAxisLabels: [],
            }
        }
        if (!formattedData || !formattedData.fData || R.isEmpty(formattedData.fData)) noData = true

        if (noData) return null

        const { fData, xAxisLabels } = formattedData
        const currency = R.pathOr('$', ['widgetRenderSettings', 'currency'], element)
        let format = R.pathOr(element.isCurrency ? 'currency' : undefined,
            ['widgetRenderSettings', 'format'], element)
        let dataFormat = format === 'currency' ? this.getCurrencySymbol(currency) : format

        if (settings.showAverage) format = 'numeric'
        if (settings.showAverage) dataFormat = 'numeric'

        const xAxisRenderLabels = xAxisLabels && Array.isArray(xAxisLabels)
            ? xAxisLabels.map((l) => R.pathOr(l, ['widgetRenderSettings', 'xAxis', l], element))
            : xAxisLabels

        const effectiveElement = settings.showAverage ? {
            ...element,
            widgetRenderSettings: {
                ...element.widgetRenderSettings,
                dataDecimals: 2,
                format: 'numeric',
            },
        } : element

        switch (element.chartType) {
            case 'npsChart':
            case 'kpiChart':
            case 'standardBar':
            case 'standardColumn': {
                const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], effectiveElement)
                const renderSettings = chartRenderSettings(effectiveElement, this, xAxisLabels, fData)
                if (['crossbreak', 'inherited'].includes(element.kpiType)) {
                    formattedData.lineData.forEach((ld, i) => (
                        renderSettings.layoutEdit.push({
                            path: ['series', i + 1],
                            value: {
                                data: ld.data.map((d) => ({ value: d.value, name: ld.name, year: undefined, benchmark: ld.name, benchmarkName: ld.name })),
                                emphasis: { focus: 'series' },
                                type: 'line',
                                yAxisIndex: element.kpiType === 'crossbreak' ? 0 : 1,
                                label: {
                                    show: false,
                                },
                                name: ld.name,
                                stack: undefined,
                            },
                        })
                    ))
                    renderSettings.layoutEdit.push({
                        path: ['legend', 'data'],
                        value: [
                            { name: fData[0].name },
                            ...formattedData.lineData.map((ld) => ({ name: ld.name })),
                        ],
                    })
                }

                if (element.kpiType === 'inherited') {
                    renderSettings.layoutEdit.push({
                        path: ['yAxis', 0],
                        value: {
                            type: 'value',
                            min: 0,
                            max: 100,
                            interval: 10,
                            axisLabel: {
                                formatter: '{value}%',
                            },
                        },
                    })
                    renderSettings.layoutEdit.push({
                        path: ['yAxis', 1],
                        value: {
                            type: 'value',
                            min: 0,
                            max: 5,
                            interval: 1,
                            axisLabel: {
                                formatter: '{value}',
                            },
                        },
                    })
                }
                if (element.kpiType === 'crossbreak') {
                    renderSettings.layoutEdit.push({
                        path: ['yAxis', 0],
                        value: {
                            type: 'value',
                            axisLabel: {
                                fontSize: 12,
                            },
                        },
                    })
                    renderSettings.layoutEdit.push({
                        path: ['yAxis', 1],
                        value: {
                            type: 'value',
                            axisLabel: {
                                fontSize: 12,
                                show: false,
                            },
                        },
                    })
                }

                return (
                    <Bar
                        xAxisLabels={renderSettings.finalxAxisLabels}
                        chartData={fData}
                        style={{ width: '100%', height: '100%' }}
                        transpose={element.chartType === 'standardBar'}
                        layoutEdit={[...renderSettings.layoutEdit]}
                        showLegend
                        dataFormat={dataFormat}
                        key={element.key}
                        tooltipDecimals={decimals}
                        yAxisDecimals={decimals}
                        defaultSeries={renderSettings.defaultSeries || {
                            type: 'bar',
                            label: {
                                show: false,
                            },
                            emphasis: {
                                focus: 'series',
                            },
                            data: [],
                        }}
                    />
                )
            }
            case 'stackedBar':
            case 'stackedColumn': {
                const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], effectiveElement)

                const renderSettings = chartRenderSettings(effectiveElement, this, xAxisLabels, fData)
                return (
                    <Bar
                        xAxisLabels={xAxisRenderLabels}
                        chartData={fData}
                        style={{ width: '100%', height: '100%' }}
                        transpose={element.chartType === 'stackedBar'}
                        showLegend
                        layoutEdit={[...renderSettings.layoutEdit]}
                        tooltipDecimals={decimals}
                        yAxisDecimals={decimals}
                        dataFormat={dataFormat}
                        key={element.key}
                        defaultSeries={renderSettings.defaultSeries || {
                            name: '',
                            type: 'bar',
                            stack: 'data',
                            label: {
                                show: false,
                            },
                            emphasis: {
                                focus: 'series',
                            },
                            data: [],
                        }}
                    />
                )
            }
            case 'line': {
                const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], effectiveElement)

                const renderSettings = chartRenderSettings(effectiveElement, this, xAxisLabels)
                return (
                    <Line
                        xAxisLabels={xAxisRenderLabels}
                        chartData={fData}
                        style={{ width: '100%', height: '100%' }}
                        showLegend
                        layoutEdit={[...renderSettings.layoutEdit]}
                        tooltipDecimals={decimals}
                        yAxisDecimals={decimals}
                        dataFormat={dataFormat}
                        key={element.key}
                        defaultSeries={renderSettings.defaultSeries || undefined}
                    />
                )
            }
            case 'lmx': {
                const sortedData = sortBy(fData, 'year', -1).sort((a, b) => {
                    if (a.benchmark && !b.benchmark) return 1
                    if (!a.benchmark && b.benchmark) return -1
                    return 0
                })
                if (element.benchmarkType === 'YOY') {
                    const latestSelectedYear = R.pathOr(0, [0, 'year'], sortBy(
                        [...this.years].filter(([_, value]) => !!value)
                            .map(([key]) => ({ year: key })),
                        'year',
                        -1,
                    ))

                    const dataLatestYear = R.pathOr(
                        R.pathOr(0, [0, 'data', 0, 'year'], sortedData),
                        [0, 'year'],
                        sortedData,
                    )

                    if (latestSelectedYear !== dataLatestYear) {
                        sortedData.unshift({
                            year: latestSelectedYear,
                            data: [
                                { value: '', name: 'No data available' },
                                { value: '', name: 'No data available' },
                                { value: '', name: 'No data available' },
                                { value: '', name: 'No data available' },
                            ],
                        })
                    }
                }

                let dataWithBenchmarks = []

                const benchmarkData = R.remove(0, 1, R.or(sortedData, [0]))
                if (benchmarkData.length) {
                    dataWithBenchmarks = R.pathOr([], [0, 'data'], sortedData).map((d, i) => ({
                        ...d,
                        value: d.value ? `${Number(d.value)}%` : '',
                        benchmarks: [],
                        // benchmarkData.map((bd, bdI) => {
                        //     if (bdI > 5) return undefined
                        //     const { className, icon } = this.compareValues(R.pathOr(0, ['value'], d), bd.data[i].value)
                        //     return (
                        //         {
                        //             name: bd.benchmarkName || bd.year,
                        //             value: (
                        //                 <span className={`benchmarkValue ${className}`}>
                        //                     {bd.data[i].value / 100}
                        //                     %
                        //                     {' '}
                        //                     {icon}
                        //                 </span>
                        //             ),
                        //         }
                        //     )
                        // }).filter((fbd) => !!fbd),
                    }))
                } else {
                    dataWithBenchmarks = R.pathOr([], [0, 'data'], sortedData)
                        .map((d) => ({ ...d, value: d.value ? `${Number(d.value)}%` : '' }))
                }

                return (
                    <MatrixChart
                        chartData={dataWithBenchmarks.map((d) => ({
                            ...d,
                            name: startCase(d.name),
                        }))}
                        style={{ width: '100%', height: '100%' }}
                        key={element.key}
                        matrixAxisNames={R.pathOr({}, ['widgetRenderSettings', 'matrixAxisNames'], element)}
                        containerSize={element.containerSize}
                    />
                )
            }
            case 'quadrantAnalysis': {
                const renderSettings = chartRenderSettings(element, this, xAxisLabels)
                const dataFormatObj = {
                    x: R.pathOr(false, ['quadrantCurrency', 'xAxis'], element) ? dataFormat : undefined,
                    y: R.pathOr(false, ['quadrantCurrency', 'yAxis'], element) ? dataFormat : undefined,
                }

                return (
                    <QuadrantAnalysis
                        chartData={fData}
                        style={{ width: '100%', height: '100%' }}
                        showLabels={false}
                        dataFormat={dataFormatObj}
                        layoutEdit={[...renderSettings.layoutEdit]}
                        key={element.key}
                    />
                )
            }
            default:
                return null
        }
    }

    getChartDefaults = (question, hasNumericCodes, hasOtherOrExclusive) => {
        const { questionTypeCode, metadata: { kpiType } } = question

        const chartType = this.chartTypesForQuestion({ questionTypeCode, kpiType, hasNumericCodes })[0]?.value

        switch (questionTypeCode) {
            case 'RD':
            case 'SB':
                return {
                    chartType,
                    sorting: (!hasNumericCodes && question?.optionSet.options?.length >= 6) ? 'highToLow' : 'noRanking',
                    removeBaseLimit: 0,
                    removeZeroNull: question?.optionSet.options?.length >= 6 && question?.numeric ? 'zero' : 'none',
                    removeZero: (!hasNumericCodes && question?.optionSet.options?.length >= 6),
                    excludeNonNumeric: null,
                    lock: (hasOtherOrExclusive && !hasNumericCodes && question?.optionSet.options?.length >= 6),
                }
            case 'CH':
                return {
                    chartType,
                    sorting: 'highToLow',
                    removeBaseLimit: 0,
                    removeZero: true,
                    removeZeroNull: 'zero',
                    excludeNonNumeric: null,
                    lock: hasOtherOrExclusive,
                }
            case 'TB':
                return {
                    chartType,
                    sorting: question?.numeric ? 'n/a' : 'highToLow',
                    removeBaseLimit: 0,
                    removeZero: true,
                    removeZeroNull: 'zero',
                    excludeNonNumeric: question?.numeric ? null : true,
                    lock: null,
                }
            case 'OD':
                return {
                    chartType,
                    sorting: 'lowToHigh',
                    removeBaseLimit: 0,
                    removeZeroNull: 'zero',
                    excludeNonNumeric: null,
                    lock: null,
                }
            case 'TA':
            case 'TX':
                return {
                    chartType,
                    sorting: 'none',
                }
            default:
                return {}
        }
    }
}
